// @flow
import * as React from 'react'
import ReactDOM from 'react-dom'
import InfoBoxFactory from 'shims/infobox_factory'
import type {
  GoogleMapsAPI,
  InfoWindow,
  GoogleMap,
  MapsEventListener,
  Marker,
} from 'types/google_maps'
import type { Icon } from './icon'

declare var ENV: any

const INFO_BOX_CLEARANCE = 10
const INFO_BOX_TARGET_WIDTH = 300
const INFO_BOX_MAX_WIDTH_PERCENTAGE = 0.75

const INFO_BOX_STYLES = {
  // set to visible to enable inner content to have box shadow
  overflow: 'visible',
  // set width here rather than in css so we can properly center the infobox
  width: `${INFO_BOX_TARGET_WIDTH}px`,
  'max-width': `${INFO_BOX_MAX_WIDTH_PERCENTAGE * 100}%`,
}

type Props = {
  gMaps: GoogleMapsAPI,
  map: GoogleMap,
  lat: number,
  lng: number,
  icon: Icon,
  children?: React.Node,
}

export default class MapEmbedMarker extends React.Component<Props> {
  contentEl: HTMLElement
  mapClickListener: MapsEventListener
  mapCloseInfoWindowsListener: MapsEventListener
  markerClickListener: MapsEventListener
  infoWindow: InfoWindow
  isInfoWindowOpen: boolean
  marker: Marker

  constructor(props: Props) {
    super(props)

    const InfoBox = InfoBoxFactory(props.gMaps)

    // this detects what the infobox width will be at the time on instantiation, if the user
    // resizes the browser this number will be off. that's an acceptable tradeoff considering the
    // complexity required to handle tracking resizes
    const mapWidth = props.map.getDiv().offsetWidth
    const infoBoxWidth = Math.min(INFO_BOX_MAX_WIDTH_PERCENTAGE * mapWidth, INFO_BOX_TARGET_WIDTH)

    this.contentEl = document.createElement('div')
    props.gMaps.OverlayView.preventMapHitsFrom(this.contentEl)

    this.marker = this.buildMarker()
    this.marker.setMap(this.props.map)

    this.infoWindow = new InfoBox({
      boxStyle: INFO_BOX_STYLES,
      content: this.contentEl,
      closeBoxURL: '',
      infoBoxClearance: new props.gMaps.Size(INFO_BOX_CLEARANCE, INFO_BOX_CLEARANCE),
      maxWidth: '75%',
      pixelOffset: new props.gMaps.Size(-(infoBoxWidth / 2), INFO_BOX_CLEARANCE),
      enableEventPropagation: true,
    })
  }

  componentDidMount = () => {
    this.updateMarkerParams()
  }

  componentDidUpdate = () => {
    this.updateMarkerParams()
  }

  componentWillUnmount = () => {
    this.unmountInfoWindowCountent()
    this.infoWindow.setMap(null)
    this.marker.setMap(null)
  }

  render = () => {
    return null
  }

  updateMarkerParams = () => {
    this.marker.setOptions(this.markerOptions)

    const firstChildMaybe = React.Children.toArray(this.props.children)[0]

    if (firstChildMaybe) {
      this.renderInfoWindowContent(firstChildMaybe)
    } else {
      this.unmountInfoWindowCountent()
    }
  }

  renderInfoWindowContent = (node: any) => {
    ReactDOM.render(node, this.contentEl)
    this.addListeners()
  }

  unmountInfoWindowCountent = () => {
    ReactDOM.unmountComponentAtNode(this.contentEl)
    this.removeListeners()
  }

  addListeners = () => {
    this.mapClickListener =
      this.mapClickListener || this.props.map.addListener('click', this.closeInfoWindow)

    this.mapCloseInfoWindowsListener =
      this.mapCloseInfoWindowsListener ||
      this.props.map.addListener('close-info-windows', this.closeInfoWindow)

    this.markerClickListener =
      this.markerClickListener ||
      this.marker.addListener('click', () => {
        // propagate the click as a close-info-windows on the map so other open markers are closed
        this.props.gMaps.event.trigger(this.props.map, 'close-info-windows')
        this.openInfoWindow()
      })
  }

  removeListeners = () => {
    if (this.mapClickListener) {
      this.mapClickListener.remove()
      this.mapClickListener = undefined
    }

    if (this.mapCloseInfoWindowsListener) {
      this.mapCloseInfoWindowsListener.remove()
      this.mapCloseInfoWindowsListener = undefined
    }

    if (this.markerClickListener) {
      this.markerClickListener.remove()
      this.markerClickListener = undefined
    }
  }

  openInfoWindow = () => {
    this.infoWindow.open(this.props.map, this.marker)
    this.isInfoWindowOpen = true
    this.marker.setOptions(this.markerOptions)
  }

  closeInfoWindow = () => {
    this.infoWindow.close()
    this.isInfoWindowOpen = false
    this.marker.setOptions(this.markerOptions)
  }

  buildMarker = () => {
    return new this.props.gMaps.Marker({
      ...this.markerOptions,
      optimized: false,
    })
  }

  get markerOptions() {
    const { gMaps, lat, lng, icon: iconType } = this.props

    const position = { lat, lng }
    let zIndex = gMaps.Marker.MAX_ZINDEX
    let icon

    if (iconType === 'brand-highlight' || this.isInfoWindowOpen) {
      icon = getBrandHighlightIcon(gMaps)
      zIndex += 1
    } else if (iconType === 'brand') {
      icon = getBrandIcon(gMaps)
    } else if (iconType === 'brand-new-fern') {
      icon = getBrandNewIcon(gMaps, 'fern')
    } else if (iconType === 'brand-new-goldenrod') {
      icon = getBrandNewIcon(gMaps, 'goldenrod')
    } else if (iconType === 'brand-new-lavender') {
      icon = getBrandNewIcon(gMaps, 'lavender')
    } else if (iconType === 'brand-new-rose') {
      icon = getBrandNewIcon(gMaps, 'rose')
    } else if (iconType === 'brand-new-skyBlue') {
      icon = getBrandNewIcon(gMaps, 'skyBlue')
    } else if (iconType === 'brand-new-slate') {
      icon = getBrandNewIcon(gMaps, 'slate')
    } else if (iconType === 'brand-new-tangerine') {
      icon = getBrandNewIcon(gMaps, 'tangerine')
    }

    return {
      position,
      zIndex,
      icon,
    }
  }
}

function getBrandIcon(gMaps: GoogleMapsAPI) {
  return {
    url: `https://ucarecdn.com/f594c370-3bb4-42d3-9223-ba51d08c2a4f/`,
    scaledSize: new gMaps.Size(39, 45),
  }
}

function getBrandNewIcon(
  gMaps: GoogleMapsAPI,
  color: 'fern' | 'goldenrod' | 'lavender' | 'rose' | 'skyBlue' | 'slate' | 'tangerine',
) {
  let url
  if (color === 'fern')
    url = 'https://ucarecdn.com/e9541d73-86d9-46f9-8b73-d3431ce176ed/-/preview/60x78/'
  if (color === 'goldenrod')
    url = 'https://ucarecdn.com/4ac9e1dc-fa6f-407f-b8c4-1753f853ca48/-/preview/60x78/'
  if (color === 'lavender')
    url = 'https://ucarecdn.com/91ae0b12-2307-4bb9-9a35-fc013c0bfe6d/-/preview/60x78/'
  if (color === 'rose')
    url = 'https://ucarecdn.com/f9c2c4d4-164e-41ae-a6dd-c045d0ecebc5/-/preview/60x78/'
  if (color === 'skyBlue')
    url = 'https://ucarecdn.com/29412595-d70d-4da1-89b0-fa30c0f1376d/-/preview/60x78/'
  if (color === 'slate')
    url = 'https://ucarecdn.com/b7704dfe-d046-496f-bd26-88baf3ff0d5e/-/preview/60x78/'
  if (color === 'tangerine')
    url = 'https://ucarecdn.com/e1c7a91f-f5e9-4395-94dc-201ccf1935d5/-/preview/60x78/'

  return {
    url,
    scaledSize: new gMaps.Size(30, 39),
  }
}

function getBrandHighlightIcon(gMaps: GoogleMapsAPI) {
  return {
    url: `https://ucarecdn.com/39338a7b-046c-4fbf-baf2-862322cdbf27/`,
    scaledSize: new gMaps.Size(39, 45),
  }
}
